import type {
  ColumnId, ColumnName,
} from '~/_shared/types/spreadsheetData/spreadsheetColumn';
import {
  type CreatorResponse, type GoogleSheetMapSyncType, type LayeringResponse, type MapIntersectWithColumnId,
  type RealSpreadsheetResponseId,
} from '~/map/map.repository';
import type { BoundaryCreateOptimizedItem } from '~/store/frontendState/mapTools/boundary/boundaryCreateOptimized/boundaryCreateOptimized.state';
import type { BoundaryCreateWMSOptimizedItem } from '~/store/frontendState/mapTools/boundary/boundaryCreateWMSOptimized/boundaryCreateWMSOptimized.state';
import type { RowRelationSpreadsheetStatus } from '~/store/rowRelations/rowRelations.types';
import { type GeocodingResponse } from './geocoding/geocoding.response';
import type { SpreadsheetId } from './spreadsheetData/spreadsheetRow';

export type MapId = number;

export type Map = Readonly<{
  id: MapId;
  parentId?: number;
  name: string;
  description?: string | null;
}>;

export enum MapPrivacyLevel {
  Private = 'private',
  Public = 'public',
  PasswordProtected = 'password_protected',
}

export type VersionDescriptor = {
  snapshot: number;
  version: number;
};

export type RealSpreadsheetVersions = {
  realSpreadSheetId: number;
  name: string;
  versions: {
    current: VersionDescriptor;
    latest: VersionDescriptor;
  };
};

export type GoogleSheetBasedRealSpreadsheetInfo = {
  autoSyncEnabled: boolean;
  googleAccountId: string | null;
  googleSheetId: string;
  googleSpreadsheetId: string;
  lastSyncType: GoogleSheetMapSyncType;
  realSpreadsheetId: number;
  spreadsheetTitle: string;
};

export type GoogleSheetBasedSpreadsheetInfo = {
  realSpreadsheets: ReadonlyArray<GoogleSheetBasedRealSpreadsheetInfo>;
  spreadsheetId: number;
};

export type OwnerMapInfo = Readonly<{
  created: CreatorResponse;
}>;

export type MapInfo = BaseMap & Readonly<{
  parentMap?: ParentMap;
  snapshots?: ReadonlyArray<MapSnapshot>;
}>;

export type MapSource = 'salesforce' | null;

export type LayeredMapBaseMap = Readonly<{
  dateCreated: Date;
  dateUpdated?: Date;
  id: number;
  isLayered: false;
  isReadOnly: boolean;
  mapViews: number;
  name: string;
  ownerId: number;
  privacy?: MapPrivacyLevel;
  shareId?: string;
  sharedUsers: ReadonlyArray<{ id: number; name: string; accessId: number }>;
  source: MapSource;
  spreadsheets: ReadonlyArray<{
    spreadsheetId: SpreadsheetId;
    realSpreadsheets: ReadonlyArray<{
      realSpreadsheetId: number;
    }>;
  }>;
}>;

export type LayeringType = Readonly<{
  additional: LayeringResponse['additional'];
  baseMaps: ReadonlyArray<LayeredMapBaseMap>;
  columns: Readonly<{
    [columnId: ColumnId]: ColumnName;
  }>;
  sourceColumns: Readonly<{
    [realSpreadsheetId: RealSpreadsheetResponseId]: Readonly<{
      [columnId: ColumnId]: ColumnName;
    }>;
  }>;
  connected: boolean;
  matches: ReadonlyArray<MapIntersectWithColumnId>;
  realTime: boolean;
}>;

type BaseMap = OwnerMapInfo & Readonly<{
  description: string | null;
  encrypted: boolean;
  googleSheets: ReadonlyArray<GoogleSheetBasedSpreadsheetInfo> | null;
  id: MapId;
  importing: boolean;
  isLayered: boolean;
  isMatchupRequired: boolean;
  isReadOnly: boolean;
  layering?: LayeringType;
  name: string;
  privacy: MapPrivacyLevel;
  publicExportDataAllowed: boolean;
  secureInProcess: boolean;
  settings: Record<string, string>;
  sharedUsers: ReadonlyArray<{ id: number; name: string; accessId: number }>;
  shareId: string;
  spreadsheets: ReadonlyArray<SpreadsheetDescriptor>;
  team: null;
  territoryGeneration: ReadonlyArray<BoundaryCreateWMSOptimizedItem | BoundaryCreateOptimizedItem>;
  updated: CreatorResponse;
  v4MigrationDone?: boolean;
  versions?: ReadonlyArray<RealSpreadsheetVersions>;
  views: number;
  source: MapSource;
}>;

export type MapSnapshot = BaseMap & Readonly<{
  parentMap: number;
  snapshots: undefined;
}>;

export type ParentMap = BaseMap & Readonly<{
  parentMap: undefined;
  snapshots: ReadonlyArray<MapSnapshot>;
}>;

export type RowRelations = Readonly<{
  [realSpreadSheetId: number]: RowRelationSpreadsheetStatus;
}>;

export type SpreadsheetDescriptor = {
  geocoding?: GeocodingResponse;
  importing: boolean;
  isEncrypted: boolean;
  isPrimary: boolean;
  privileges: 'modify';
  realSpreadsheets: ReadonlyArray<{
    realSpreadSheetId: number;
    addRowLink?: string;
  }>;
  rowRelations: RowRelations;
  spreadSheetId: number;
};
